import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getCompanySettings, updateCompanySettings } from '../../services/companySettingsService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import GenericSelect from '../../components/ui/GenericSelect';
import { formatDate } from '../../utils/Utils';
import { DEFAULT_TIME_ZONE_ID, timeZoneOptions } from '../../utils/timeZones';

const Wrapper = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  max-width: 640px;
  margin-top: 16px;
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;
`;

const Description = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6B7280;
  margin: 0 0 16px;
`;

const Toggles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TemplateBlock = styled.div`
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TemplateTitle = styled.h4`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Hint = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  margin: 8px 0 0;

  code {
    background: #F5F7FA;
    border-radius: 3px;
    padding: 1px 5px;
  }
`;

const PolicyFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

// i18next {{...}} interpolasyonuyla çakışmaması için token listesi ÇEVİRİ DEĞİL,
// sabit JSX literalidir (dil bağımsız değişken adları).
const TOKEN_SAMPLE = '{{ad}} {{adsoyad}} {{yil}} {{sirket}}';

// Şifre politikası sınırları — sunucu yanıtı taşır (CompanySettingsResponse); yanıt eski bir
// sunucudan gelirse aynı değerlere düşülür. Sunucu kayıtta yeniden doğrular.
const PASSWORD_LIMIT_DEFAULTS = { floor: 8, ceiling: 64, expiryOptions: [0, 3, 6, 12] };

// Oturum güvenliği — sunucu yanıtı seçenekleri taşır (SessionSecurityPolicy); eski sunucuda bunlar.
const SESSION_DEFAULTS = { idleOptions: [15, 30, 60], idleMinutes: 30, trustedBrowserDays: 30 };

// AKM POC §9.2 / §9.1 — hesap kilidi ve işlem geçmişi saklama süresi; seçenekler sunucu yanıtından, eski sunucuda bunlar.
const LOCKOUT_DEFAULTS = {
  thresholdOptions: [3, 5, 10], windowOptions: [5, 15, 30, 60], durationOptions: [5, 15, 30, 60],
  retentionOptions: [24, 36, 48, 60, 84, 120], threshold: 5, windowMinutes: 15, durationMinutes: 15, retentionMonths: 24,
};
const optionsOr = (list, fallback) => (Array.isArray(list) && list.length ? list : fallback);

export default function CompanySettingsPage() {
  const { t, i18n } = useTranslation();
  const { canWrite, isEnterpriseAdmin } = useAuth();
  const readOnly = !canWrite('EM');
  // Saklama süresini yalnız şirket yöneticisi değiştirir (sunucu da 403 döner); diğerlerine salt-okunur metin.
  const retentionEditable = !readOnly && Boolean(isEnterpriseAdmin);

  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [passwordLimits, setPasswordLimits] = useState(PASSWORD_LIMIT_DEFAULTS);
  const [minLengthError, setMinLengthError] = useState('');
  const [defaultTimeZoneId, setDefaultTimeZoneId] = useState(DEFAULT_TIME_ZONE_ID);
  const [sessionLimits, setSessionLimits] = useState(SESSION_DEFAULTS);
  const [lockoutLimits, setLockoutLimits] = useState(LOCKOUT_DEFAULTS);
  // Zorunluluğu açmanın ön koşulu: kaydedenin kendi iki adımlı doğrulaması etkin olmalı.
  const [currentUserMfaEnabled, setCurrentUserMfaEnabled] = useState(false);

  useEffect(() => {
    getCompanySettings().then((res) => {
      const s = res.data?.data;
      if (!s) return;
      setSettings({
        birthdayEmailEnabled: s.birthdayEmailEnabled === true,
        anniversaryEmailEnabled: s.anniversaryEmailEnabled === true,
        employeePhotoChangeEnabled: s.employeePhotoChangeEnabled !== false,
        leaveAccessBlockEnabled: s.leaveAccessBlockEnabled === true,
        birthdayEmailSubject: s.birthdayEmailSubject || '',
        birthdayEmailBody: s.birthdayEmailBody || '',
        anniversaryEmailSubject: s.anniversaryEmailSubject || '',
        anniversaryEmailBody: s.anniversaryEmailBody || '',
        passwordMinLength: String(s.passwordMinLength ?? PASSWORD_LIMIT_DEFAULTS.floor),
        passwordRequireAlphanumeric: s.passwordRequireAlphanumeric === true,
        passwordRequireSpecialCharacter: s.passwordRequireSpecialCharacter === true,
        passwordExpiryMonths: Number(s.passwordExpiryMonths) || 0,
        passwordExpiryStartedAt: s.passwordExpiryStartedAt || null,
        // '' = ayarlanmamış (sunucuda NULL → varsayılan dilim). Kayıtta HER ZAMAN gönderilir:
        // sunucu yalnız alan hiç gelmezse (bayat arayüz) mevcut değeri korur.
        timeZoneId: s.timeZoneId || '',
        mfaEnabled: s.mfaEnabled === true,
        mfaTrustedBrowserEnabled: s.mfaTrustedBrowserEnabled !== false,
        sessionIdleTimeoutMinutes: Number(s.sessionIdleTimeoutMinutes) || SESSION_DEFAULTS.idleMinutes,
        loginLockoutThreshold: Number(s.loginLockoutThreshold) || LOCKOUT_DEFAULTS.threshold,
        loginLockoutWindowMinutes: Number(s.loginLockoutWindowMinutes) || LOCKOUT_DEFAULTS.windowMinutes,
        loginLockoutDurationMinutes: Number(s.loginLockoutDurationMinutes) || LOCKOUT_DEFAULTS.durationMinutes,
        auditRetentionMonths: Number(s.auditRetentionMonths) || LOCKOUT_DEFAULTS.retentionMonths,
      });
      setLockoutLimits({
        ...LOCKOUT_DEFAULTS,
        thresholdOptions: optionsOr(s.loginLockoutThresholdOptions, LOCKOUT_DEFAULTS.thresholdOptions),
        windowOptions: optionsOr(s.loginLockoutWindowOptions, LOCKOUT_DEFAULTS.windowOptions),
        durationOptions: optionsOr(s.loginLockoutDurationOptions, LOCKOUT_DEFAULTS.durationOptions),
        retentionOptions: optionsOr(s.auditRetentionOptions, LOCKOUT_DEFAULTS.retentionOptions),
      });
      setCurrentUserMfaEnabled(s.currentUserMfaEnabled === true);
      setSessionLimits({
        idleOptions: Array.isArray(s.sessionIdleTimeoutOptions) && s.sessionIdleTimeoutOptions.length
          ? s.sessionIdleTimeoutOptions
          : SESSION_DEFAULTS.idleOptions,
        trustedBrowserDays: s.mfaTrustedBrowserDays || SESSION_DEFAULTS.trustedBrowserDays,
      });
      setDefaultTimeZoneId(s.defaultTimeZoneId || DEFAULT_TIME_ZONE_ID);
      setPasswordLimits({
        floor: s.passwordMinLengthFloor ?? PASSWORD_LIMIT_DEFAULTS.floor,
        ceiling: s.passwordMinLengthCeiling ?? PASSWORD_LIMIT_DEFAULTS.ceiling,
        expiryOptions: Array.isArray(s.passwordExpiryOptions) && s.passwordExpiryOptions.length
          ? s.passwordExpiryOptions
          : PASSWORD_LIMIT_DEFAULTS.expiryOptions,
      });
      setDefaults({
        birthdaySubject: s.defaultBirthdaySubject || '',
        birthdayBody: s.defaultBirthdayBody || '',
        anniversarySubject: s.defaultAnniversarySubject || '',
        anniversaryBody: s.defaultAnniversaryBody || '',
      });
    });
  }, []);

  const setField = (field) => (value) => {
    if (readOnly) return;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = useCallback(async () => {
    const minLength = Number(settings.passwordMinLength);
    if (!Number.isInteger(minLength) || minLength < passwordLimits.floor || minLength > passwordLimits.ceiling) {
      setMinLengthError(t('companySettings.passwordMinLengthRange', { min: passwordLimits.floor, max: passwordLimits.ceiling }));
      return;
    }
    setMinLengthError('');
    setSaving(true);
    try {
      // Sayacın başlangıç damgası sunucunundur; istek gövdesine geri yollanmaz.
      const payload = { ...settings, passwordMinLength: minLength, timeZoneId: settings.timeZoneId || '' };
      delete payload.passwordExpiryStartedAt;
      const res = await updateCompanySettings(payload);
      const saved = res?.data?.data;
      // Aralık değiştiyse sunucu sayacın başlangıcını yeniden damgalar — bilgi satırı güncel kalsın.
      if (saved) {
        setSettings((prev) => ({
          ...prev,
          passwordExpiryStartedAt: saved.passwordExpiryStartedAt || null,
          timeZoneId: saved.timeZoneId || '',
        }));
        setCurrentUserMfaEnabled(saved.currentUserMfaEnabled === true);
      }
    } catch { /* interceptor */ }
    setSaving(false);
  }, [settings, passwordLimits, t]);

  if (!settings) return null;

  const expiryLabel = (months) => (months === 0
    ? t('companySettings.passwordExpiryNever')
    : t('companySettings.passwordExpiryEvery', { count: months }));
  const expiryOptions = passwordLimits.expiryOptions.map((m) => ({ id: m, name: expiryLabel(m) }));
  const timeZonePlaceholder = t('companySettings.timeZoneDefault', { zone: defaultTimeZoneId });
  const idleOptions = sessionLimits.idleOptions.map((m) => ({ id: m, name: t('companySettings.idleTimeoutMinutes', { count: m }) }));
  // Kapalıyken açmak için kendi MFA'n etkin olmalı (sunucu da reddeder); açıkken kapatmak serbest.
  const mfaLocked = !settings.mfaEnabled && !currentUserMfaEnabled;
  const minutesLabel = (m) => t('companySettings.minutes', { count: m });
  const toOptions = (list, label) => list.map((v) => ({ id: v, name: label(v) }));
  const thresholdOptions = toOptions(lockoutLimits.thresholdOptions, (v) => String(v));
  const windowOptions = toOptions(lockoutLimits.windowOptions, minutesLabel);
  const durationOptions = toOptions(lockoutLimits.durationOptions, minutesLabel);
  const retentionLabel = (m) => t('companySettings.auditRetentionMonths', { count: m });
  const retentionOptions = toOptions(lockoutLimits.retentionOptions, retentionLabel);
  const lockoutField = (field, label, options, display, fallback) => (readOnly ? (
    <TextInput label={label} value={display(settings[field])} disabled />
  ) : (
    <GenericSelect
      label={label}
      options={options}
      value={settings[field]}
      onChange={(v) => setField(field)(v ?? fallback)}
      idKey="id"
      labelKey="name"
      clearable={false}
    />
  ));

  return (
    <Wrapper>
      <Breadcrumbs items={[{ label: t('companySettings.title') }]} />

      <Card>
        <SectionTitle>{t('companySettings.celebrationSection')}</SectionTitle>
        <Description>{t('companySettings.celebrationDescription')}</Description>
        <Toggles>
          <CheckboxInput
            label={t('companySettings.birthdayEnabled')}
            checked={settings.birthdayEmailEnabled}
            onChange={setField('birthdayEmailEnabled')}
          />
          <CheckboxInput
            label={t('companySettings.anniversaryEnabled')}
            checked={settings.anniversaryEmailEnabled}
            onChange={setField('anniversaryEmailEnabled')}
          />
        </Toggles>

        {settings.birthdayEmailEnabled && (
          <TemplateBlock>
            <TemplateTitle>{t('companySettings.birthdayTemplate')}</TemplateTitle>
            <TextInput
              label={t('companySettings.subject')}
              value={settings.birthdayEmailSubject}
              onChange={(e) => setField('birthdayEmailSubject')(e.target.value)}
              placeholder={defaults.birthdaySubject}
              disabled={readOnly}
            />
            <TextInput
              label={t('companySettings.body')}
              type="textarea"
              rows={4}
              value={settings.birthdayEmailBody}
              onChange={(e) => setField('birthdayEmailBody')(e.target.value)}
              placeholder={defaults.birthdayBody}
              disabled={readOnly}
            />
          </TemplateBlock>
        )}

        {settings.anniversaryEmailEnabled && (
          <TemplateBlock>
            <TemplateTitle>{t('companySettings.anniversaryTemplate')}</TemplateTitle>
            <TextInput
              label={t('companySettings.subject')}
              value={settings.anniversaryEmailSubject}
              onChange={(e) => setField('anniversaryEmailSubject')(e.target.value)}
              placeholder={defaults.anniversarySubject}
              disabled={readOnly}
            />
            <TextInput
              label={t('companySettings.body')}
              type="textarea"
              rows={4}
              value={settings.anniversaryEmailBody}
              onChange={(e) => setField('anniversaryEmailBody')(e.target.value)}
              placeholder={defaults.anniversaryBody}
              disabled={readOnly}
            />
          </TemplateBlock>
        )}

        {(settings.birthdayEmailEnabled || settings.anniversaryEmailEnabled) && (
          <Hint>
            {t('companySettings.templateHint')} <code>{TOKEN_SAMPLE}</code>
          </Hint>
        )}
      </Card>

      <Card>
        <SectionTitle>{t('companySettings.photoSection')}</SectionTitle>
        <Description>{t('companySettings.photoDescription')}</Description>
        <CheckboxInput
          label={t('companySettings.photoChangeEnabled')}
          checked={settings.employeePhotoChangeEnabled}
          onChange={setField('employeePhotoChangeEnabled')}
        />
      </Card>

      <Card>
        <SectionTitle>{t('companySettings.accessSection')}</SectionTitle>
        <Description>{t('companySettings.leaveAccessBlockDescription')}</Description>
        <CheckboxInput
          label={t('companySettings.leaveAccessBlockEnabled')}
          checked={settings.leaveAccessBlockEnabled}
          onChange={setField('leaveAccessBlockEnabled')}
        />
      </Card>

      <Card>
        <SectionTitle>{t('companySettings.timeZoneSection')}</SectionTitle>
        <Description>{t('companySettings.timeZoneDescription')}</Description>
        {readOnly ? (
          <TextInput
            label={t('companySettings.timeZone')}
            value={settings.timeZoneId || timeZonePlaceholder}
            disabled
          />
        ) : (
          <GenericSelect
            label={t('companySettings.timeZone')}
            placeholder={timeZonePlaceholder}
            options={timeZoneOptions(settings.timeZoneId)}
            value={settings.timeZoneId}
            onChange={(v) => setField('timeZoneId')(v || '')}
            idKey="id"
            labelKey="name"
          />
        )}
      </Card>

      <Card>
        <SectionTitle>{t('companySettings.sessionSecuritySection')}</SectionTitle>
        <Description>{t('companySettings.sessionSecurityDescription')}</Description>
        <PolicyFields>
          <div>
            <CheckboxInput
              label={t('companySettings.mfaEnabled')}
              checked={settings.mfaEnabled}
              onChange={setField('mfaEnabled')}
              disabled={readOnly || mfaLocked}
            />
            <Hint>{t('companySettings.mfaEnabledHint')}</Hint>
            {mfaLocked && !readOnly && (
              <Hint data-testid="mfa-own-enrollment-hint">
                {t('companySettings.mfaRequiresOwnEnrollment')}
                {' '}
                <Link to="/profile">{t('companySettings.mfaGoToProfile')}</Link>
              </Hint>
            )}
          </div>
          <div>
            <CheckboxInput
              label={t('companySettings.mfaTrustedBrowserEnabled', { count: sessionLimits.trustedBrowserDays })}
              checked={settings.mfaTrustedBrowserEnabled}
              onChange={setField('mfaTrustedBrowserEnabled')}
              disabled={readOnly}
            />
            <Hint>{t('companySettings.mfaTrustedBrowserHint')}</Hint>
          </div>
          <div>
            {readOnly ? (
              <TextInput
                label={t('companySettings.idleTimeout')}
                value={t('companySettings.idleTimeoutMinutes', { count: settings.sessionIdleTimeoutMinutes })}
                disabled
              />
            ) : (
              <GenericSelect
                label={t('companySettings.idleTimeout')}
                options={idleOptions}
                value={settings.sessionIdleTimeoutMinutes}
                onChange={(v) => setField('sessionIdleTimeoutMinutes')(v ?? SESSION_DEFAULTS.idleMinutes)}
                idKey="id"
                labelKey="name"
                clearable={false}
              />
            )}
            <Hint>{t('companySettings.idleTimeoutHint')}</Hint>
          </div>
        </PolicyFields>
      </Card>

      <Card data-testid="login-lockout-card">
        <SectionTitle>{t('companySettings.loginLockoutSection')}</SectionTitle>
        <Description>{t('companySettings.loginLockoutHint')}</Description>
        <PolicyFields>
          {lockoutField('loginLockoutThreshold', t('companySettings.loginLockoutThreshold'), thresholdOptions,
            (v) => String(v), LOCKOUT_DEFAULTS.threshold)}
          {lockoutField('loginLockoutWindowMinutes', t('companySettings.loginLockoutWindow'), windowOptions,
            minutesLabel, LOCKOUT_DEFAULTS.windowMinutes)}
          {lockoutField('loginLockoutDurationMinutes', t('companySettings.loginLockoutDuration'), durationOptions,
            minutesLabel, LOCKOUT_DEFAULTS.durationMinutes)}
          <div data-testid="audit-retention">
            {retentionEditable ? (
              <GenericSelect
                label={t('companySettings.auditRetention')}
                options={retentionOptions}
                value={settings.auditRetentionMonths}
                onChange={(v) => setField('auditRetentionMonths')(v ?? LOCKOUT_DEFAULTS.retentionMonths)}
                idKey="id"
                labelKey="name"
                clearable={false}
              />
            ) : (
              <TextInput
                label={t('companySettings.auditRetention')}
                value={retentionLabel(settings.auditRetentionMonths)}
                disabled
              />
            )}
            <Hint>{t('companySettings.auditRetentionHint')}</Hint>
          </div>
        </PolicyFields>
      </Card>

      <Card>
        <SectionTitle>{t('companySettings.passwordSection')}</SectionTitle>
        <Description>{t('companySettings.passwordDescription')}</Description>
        <PolicyFields>
          <div>
            <TextInput
              label={t('companySettings.passwordMinLength')}
              type="number"
              min={passwordLimits.floor}
              max={passwordLimits.ceiling}
              step={1}
              value={settings.passwordMinLength}
              onChange={(e) => { setMinLengthError(''); setField('passwordMinLength')(e.target.value); }}
              disabled={readOnly}
              error={minLengthError}
            />
            <Hint>{t('companySettings.passwordMinLengthRange', { min: passwordLimits.floor, max: passwordLimits.ceiling })}</Hint>
          </div>
          <Toggles>
            <CheckboxInput
              label={t('companySettings.passwordRequireAlphanumeric')}
              checked={settings.passwordRequireAlphanumeric}
              onChange={setField('passwordRequireAlphanumeric')}
            />
            <CheckboxInput
              label={t('companySettings.passwordRequireSpecialCharacter')}
              checked={settings.passwordRequireSpecialCharacter}
              onChange={setField('passwordRequireSpecialCharacter')}
            />
          </Toggles>
          <div>
            {readOnly ? (
              <TextInput
                label={t('companySettings.passwordExpiry')}
                value={expiryLabel(settings.passwordExpiryMonths)}
                disabled
              />
            ) : (
              <GenericSelect
                label={t('companySettings.passwordExpiry')}
                options={expiryOptions}
                value={settings.passwordExpiryMonths}
                onChange={(v) => setField('passwordExpiryMonths')(v ?? 0)}
                idKey="id"
                labelKey="name"
                clearable={false}
              />
            )}
            <Hint>{t('companySettings.passwordExpiryHint')}</Hint>
            {settings.passwordExpiryMonths > 0 && settings.passwordExpiryStartedAt && (
              <Hint data-testid="password-expiry-started">
                {t('companySettings.passwordExpiryStartedAt', { date: formatDate(settings.passwordExpiryStartedAt, i18n.language) })}
              </Hint>
            )}
          </div>
          <Hint>{t('companySettings.passwordScopeHint')}</Hint>
        </PolicyFields>
      </Card>

      {!readOnly && (
        <Actions>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? '...' : t('common.save')}
          </Button>
        </Actions>
      )}
    </Wrapper>
  );
}
